import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { authAPI } from '../services/api';
import { locationAPI } from '../services/api';
import Select from "react-select";
import { toast } from '../hooks/use-toast';
import { Loader2, UserPlus, Eye, EyeOff } from 'lucide-react';

const Register = () => {

  const navigate = useNavigate();

  const [loading,setLoading] = useState(false);
  const [showPassword,setShowPassword] = useState(false);
  const [locations,setLocations] = useState([]);

  const [formData,setFormData] = useState({
    email:'',
    password:'',
    confirmPassword:'',
    fullName:'',
    city:''
  });

  // Load Cities
  useEffect(()=>{
    loadLocations();
  },[]);

const loadLocations = async () => {
  try {
    const res = await locationAPI.getAll();

    console.log("API Response:", res); // debug

    const data = Array.isArray(res) ? res : [res];

    setLocations(data.filter(loc => loc.isActive)); // optional
  } catch (error) {
    console.error("Error loading locations", error);
  }
};

  const handleChange = (e)=>{
    setFormData({
      ...formData,
      [e.target.name]:e.target.value
    });
  };

  // Password Strength
  const getPasswordStrength = (password)=>{

    if(password.length < 6)
      return {label:"Weak",color:"red"};

    if(password.match(/[A-Z]/) && password.match(/[0-9]/) && password.length >=8)
      return {label:"Strong",color:"green"};

    return {label:"Medium",color:"orange"};
  };

  const strength = getPasswordStrength(formData.password);

  const handleSubmit = async (e)=>{

    e.preventDefault();

    if(!formData.email || !formData.password || !formData.fullName){

      toast({
        title:'Error',
        description:'Please fill all required fields',
        variant:'destructive'
      });

      return;
    }

    if(formData.password !== formData.confirmPassword){

      toast({
        title:'Error',
        description:'Passwords do not match',
        variant:'destructive'
      });

      return;
    }

    try{

      setLoading(true);

      const response = await authAPI.register({
        email:formData.email,
        password:formData.password,
        fullName:formData.fullName,
        city:formData.city
      });

      toast({
        title:'Registration Successful',
        description:'Account created successfully'
      });

      localStorage.setItem('authToken',response.access_token);
      localStorage.setItem('userEmail',response.email);
      localStorage.setItem('userFullName',response.fullName);
      localStorage.setItem('userRole',response.role || 'job_seeker');

      navigate(`/edit-profile/${response.email}`);

    }
    catch(error){

      toast({
        title:'Registration Failed',
        description:error.response?.data?.detail || 'Something went wrong',
        variant:'destructive'
      });

    }
    finally{
      setLoading(false);
    }

  };

  return (

    <div className="min-h-screen bg-gray-50">

      <Header/>

      <div className="max-w-md mx-auto px-6 py-20">

        <Card className="p-8">

          <div className="text-center mb-8">

            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-[#4a90e2]" />
            </div>

            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Create Account
            </h1>

            <p className="text-gray-600">
              Register as a job seeker
            </p>

          </div>


          <form onSubmit={handleSubmit} className="space-y-6">

            <div>
              <Label>Full Name *</Label>
              <Input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </div>


            {/* CITY SEARCHABLE DROPDOWN */}

            <div>
              <Label>City *</Label>

              <Select
                options={locations.map(loc => ({
                  value: loc.city,
                  label: `${loc.city}, ${loc.state}`
                }))}

                onChange={(selected)=>{
                  setFormData({
                    ...formData,
                    city:selected.value
                  });
                }}

                placeholder="Search your city..."

                styles={{
                  control:(base)=>({
                    ...base,
                    borderColor:"#e5e7eb",
                    minHeight:"42px"
                  })
                }}
              />

            </div>


            {/* PASSWORD */}

            <div>

              <Label>Password *</Label>

              <div className="relative">

                <Input
                  type={showPassword ? "text":"password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  required
                />

                <span
                  className="absolute right-3 top-3 cursor-pointer"
                  onClick={()=>setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </span>

              </div>

              {formData.password && (

                <p className="text-sm mt-1" style={{color:strength.color}}>
                  Password Strength: {strength.label}
                </p>

              )}

            </div>


            <div>
              <Label>Confirm Password *</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                required
              />
            </div>


            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4a90e2] hover:bg-[#3a7bc8] text-white font-semibold h-12"
            >

              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
                  Creating Account...
                </>
              ):(
                'Register'
              )}

            </Button>

          </form>


          <div className="mt-6 text-center">

            <p className="text-sm text-gray-600">

              Already have an account?{" "}

              <Link
                to="/login"
                className="text-[#4a90e2] hover:underline font-medium"
              >
                Login here
              </Link>

            </p>

          </div>

        </Card>

      </div>

      <Footer/>

    </div>

  );

};

export default Register;